import { useCallback, useEffect, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";
import type { SplitShiftConfig, HolidaySyncStatus } from "@/types/companySettings";
import { DEFAULT_SPLIT_SHIFT_CONFIG } from "@/types/companySettings";

// Scheduling-specific company settings (subset of full company_settings table)
interface SchedulingSettings {
  company_id: string;
  enable_offers: boolean;
  enable_swaps: boolean;
  require_approval_offers: boolean;
  require_approval_swaps: boolean;
  approver_roles: string[];
  swap_auto_expire_hours: number | null;
  swap_requires_approval: boolean | null;
  split_shift_config: SplitShiftConfig;
  holidays?: string[]; // ISO date strings (YYYY-MM-DD)
  country_code?: string | null;
  region_code?: string | null;
  holiday_sync_status?: HolidaySyncStatus;
  holiday_auto_sync?: boolean;
  updated_at: string;
}

type CompanySettings = SchedulingSettings;

// Stable defaults object to prevent re-renders
const DEFAULTS: CompanySettings = {
  company_id: "",
  enable_offers: true,
  enable_swaps: true,
  require_approval_offers: false,
  require_approval_swaps: true,
  approver_roles: ['ADMIN', 'MANAGER', 'SUPERVISOR'],
  swap_auto_expire_hours: null,
  swap_requires_approval: null,
  split_shift_config: DEFAULT_SPLIT_SHIFT_CONFIG,
  updated_at: new Date().toISOString(),
};

export function useCompanySettings() {
  const { currentCompany } = useAuth();
  const companyId = currentCompany?.id;
  const lastFetchedAtRef = useRef<number>(0);
  const STALE_THRESHOLD_MS = 30_000; // 30 seconds
  
  const [settings, setSettings] = useState<CompanySettings>(() => {
    if (!companyId) return DEFAULTS;
    
    // Try to load from localStorage for instant UX
    const lsKey = `company_settings::${companyId}::v1`;
    const cached = localStorage.getItem(lsKey);
    if (cached) {
      try {
        const parsed = JSON.parse(cached);
        return { ...DEFAULTS, ...parsed };
      } catch {
        // Invalid cache, use defaults
      }
    }
    return DEFAULTS;
  });
  
  const [isLoading, setIsLoading] = useState(false);

  const refresh = useCallback(async () => {
    if (!companyId) return;
    
    setIsLoading(true);
    try {
      const { data, error } = await supabase.rpc("get_company_settings");
      
      if (!error && data) {
        const newSettings = { 
          ...DEFAULTS, 
          ...data,
          split_shift_config: (data.split_shift_config as unknown as CompanySettings['split_shift_config']) || DEFAULTS.split_shift_config,
          holidays: (data.holidays as string[]) || [],
          holiday_sync_status: (data.holiday_sync_status as unknown as HolidaySyncStatus) || undefined,
        };
        setSettings(newSettings as CompanySettings);
        lastFetchedAtRef.current = Date.now();
        
        const lsKey = `company_settings::${companyId}::v1`;
        localStorage.setItem(lsKey, JSON.stringify(newSettings));
      }
    } catch (error) {
      console.error("Error fetching company settings:", error);
    } finally {
      setIsLoading(false);
    }
  }, [companyId]);

  const updateSettings = useCallback(async (patch: Partial<Pick<CompanySettings, 'enable_offers' | 'enable_swaps' | 'require_approval_offers' | 'require_approval_swaps' | 'approver_roles' | 'split_shift_config' | 'holidays' | 'region_code' | 'country_code' | 'holiday_sync_status' | 'holiday_auto_sync'>>) => {
    if (!companyId) throw new Error("No company ID available");
    
    const previousSettings = settings;
    const newSettings = { ...settings, ...patch };
    setSettings(newSettings);
    
    const lsKey = `company_settings::${companyId}::v1`;
    localStorage.setItem(lsKey, JSON.stringify(newSettings));
    
    try {
      const { data, error } = await supabase.rpc("set_company_settings", {
        p_enable_offers: patch.enable_offers,
        p_enable_swaps: patch.enable_swaps,
        p_require_approval_offers: patch.require_approval_offers,
        p_require_approval_swaps: patch.require_approval_swaps,
        p_approver_roles: patch.approver_roles,
        p_split_shift_config: patch.split_shift_config ? JSON.parse(JSON.stringify(patch.split_shift_config)) : undefined,
        p_holidays: patch.holidays,
        p_country_code: patch.country_code,
        p_region_code: patch.region_code,
        p_holiday_sync_status: patch.holiday_sync_status ? JSON.parse(JSON.stringify(patch.holiday_sync_status)) : undefined,
        p_holiday_auto_sync: patch.holiday_auto_sync,
      });
      
      if (error) throw error;
      
      if (data) {
        const finalSettings = { 
          ...DEFAULTS, 
          ...data,
          split_shift_config: (data.split_shift_config as unknown as CompanySettings['split_shift_config']) || DEFAULTS.split_shift_config,
          holidays: (data.holidays as string[]) || [],
          holiday_sync_status: (data.holiday_sync_status as unknown as HolidaySyncStatus) || undefined,
        };
        setSettings(finalSettings as CompanySettings);
        lastFetchedAtRef.current = Date.now();
        localStorage.setItem(lsKey, JSON.stringify(finalSettings));
      }
    } catch (error) {
      setSettings(previousSettings);
      localStorage.setItem(lsKey, JSON.stringify(previousSettings));
      throw error;
    }
  }, [companyId, settings, refresh]);

  // Fetch settings when company changes
  useEffect(() => {
    if (companyId) {
      refresh();
    } else {
      setSettings(DEFAULTS);
    }
  }, [companyId, refresh]);

  // Cross-tab sync: listen for localStorage changes from other tabs
  useEffect(() => {
    if (!companyId) return;
    const lsKey = `company_settings::${companyId}::v1`;

    const handleStorageChange = (e: StorageEvent) => {
      if (e.key !== lsKey || !e.newValue) return;
      try {
        const parsed = JSON.parse(e.newValue);
        setSettings({ ...DEFAULTS, ...parsed });
      } catch {
        // Corrupt value — trigger a fresh server fetch instead
        refresh();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [companyId, refresh]);

  // Refresh on tab focus if data is stale
  useEffect(() => {
    if (!companyId) return;

    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible') return;
      if (Date.now() - lastFetchedAtRef.current < STALE_THRESHOLD_MS) return;
      refresh();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [companyId, refresh]);

  return {
    settings,
    isLoading,
    refresh,
    updateSettings,
  };
}

// Centralized feature flag utility functions
export const isOffersEnabled = (settings: CompanySettings | null | undefined): boolean => 
  settings?.enable_offers ?? false;

export const isSwapsEnabled = (settings: CompanySettings | null | undefined): boolean => 
  settings?.enable_swaps ?? true;
